<template>
  <div class="identify-page">
    <div class="identify-container">
      <!-- Logo -->
      <div class="logo-section" v-if="siteInfo && siteInfo.LogoUrl">
        <img class="logo" :src="siteInfo.LogoUrl" alt="Practice Logo"/>
      </div>

      <div class="identify-card" v-if="siteInfo || loadFailed">
        <div class="card-header">
          <h1 class="card-title">{{ title }}</h1>
          <p class="card-subtitle">{{ step === 1 ? t('subtitle') : t('subtitleCode') }}</p>
        </div>

        <div class="card-body">
          <div v-if="message" class="message" :class="messageIsError ? 'message-error' : 'message-info'" role="alert" data-test="identify-message">
            {{ message }}
          </div>

          <!-- Step 1: date of birth + name (nothing but the message when the link could not be loaded) -->
          <form v-if="!loadFailed && step === 1" @submit.prevent="identify" novalidate data-test="identify-step-1">
            <div class="form-group">
              <label for="identify-dob"><span class="label-text">{{ t('dobLabel') }}</span></label>
              <div class="input-wrapper">
                <i class="la la-calendar input-icon"></i>
                <input
                  id="identify-dob"
                  v-model="dob"
                  type="text"
                  class="styled-input"
                  :placeholder="siteInfo.DateFormat"
                  autocomplete="off"
                  data-test="identify-dob"
                />
              </div>
            </div>

            <div class="form-group">
              <label for="identify-first-name"><span class="label-text">{{ t('firstNameLabel') }}</span></label>
              <div class="input-wrapper">
                <i class="la la-user input-icon"></i>
                <input
                  id="identify-first-name"
                  v-model="firstName"
                  type="text"
                  class="styled-input"
                  autocomplete="given-name"
                  data-test="identify-first-name"
                />
              </div>
            </div>

            <div class="form-group">
              <label for="identify-last-name"><span class="label-text">{{ t('lastNameLabel') }}</span></label>
              <div class="input-wrapper">
                <i class="la la-user input-icon"></i>
                <input
                  id="identify-last-name"
                  v-model="lastName"
                  type="text"
                  class="styled-input"
                  autocomplete="family-name"
                  data-test="identify-last-name"
                />
              </div>
            </div>

            <button type="submit" class="primary-btn" :disabled="loading" data-test="identify-submit">
              <i v-if="loading" class="fa fa-spinner fa-spin mr-2"></i>
              {{ loading ? t('finding') : t('findButton') }}
            </button>
          </form>

          <!-- Step 2: one-time code -->
          <form v-else-if="!loadFailed" @submit.prevent="verifyCode" novalidate data-test="identify-step-2">
            <p class="code-sent" data-test="identify-code-sent">
              {{ t(channel === 'email' ? 'codeSentEmail' : 'codeSentSms', { destination: maskedDestination }) }}
            </p>

            <div class="form-group">
              <label for="identify-code"><span class="label-text">{{ t('codeLabel') }}</span></label>
              <input
                id="identify-code"
                :value="code"
                @input="onCodeInput"
                type="text"
                class="styled-input code-input"
                inputmode="numeric"
                pattern="[0-9]*"
                autocomplete="one-time-code"
                maxlength="6"
                data-test="identify-code"
              />
            </div>

            <button type="submit" class="primary-btn" :disabled="loading || resending" data-test="identify-continue">
              <i v-if="loading" class="fa fa-spinner fa-spin mr-2"></i>
              {{ loading ? t('verifying') : t('continueButton') }}
            </button>

            <div class="secondary-actions">
              <button type="button" class="link-btn" :disabled="loading || resending" @click="resend" data-test="identify-resend">
                {{ resending ? t('resending') : t('resendButton') }}
              </button>
              <button type="button" class="link-btn" :disabled="loading || resending" @click="startOver(null)" data-test="identify-start-over">
                {{ t('startOver') }}
              </button>
            </div>
          </form>
        </div>

        <!-- Office information -->
        <div class="office-info-section" v-if="getSiteDetails()">
          <div class="office-details">
            <span v-html="getSiteDetails()"></span>
          </div>
        </div>
      </div>

      <!-- Loading -->
      <div class="loading-state" v-if="!siteInfo && !loadFailed">
        <div class="spinner"></div>
        <p>{{ t('loading') }}</p>
      </div>
      <PoweredByFooter />
    </div>
  </div>
</template>

<script>
/**
 * "Existing patients" website link — public identify page.
 *
 * Flow (Documents/forms-existing-patient-links.md, Part B):
 *   /l/<generic urlID> (UrlRedirect, linkMode 'existing') → HERE
 *   step 1: DOB + first + last name → POST /identify-patient-for-form → { challengeId, channel, maskedDestination }
 *   step 2: 6-digit code → POST /verify-identify-code → { urlID } = the PATIENT-SPECIFIC short-link id
 *   → sessionStorage['teamio_identified_<urlID>'] = '1' → $router.replace('/l/<urlID>')
 *   → UrlRedirect consumes the marker and routes that link as already verified (prefilled form,
 *     submission auto-linked; packets chain with packetVerified=1).
 *
 * siteID arrives as a route param from UrlRedirect; on a refresh params are gone, so it is re-resolved
 * from the generic urlID via get-long-url.
 *
 * ⛔ No PHI in logs: never console.log the payload, the axios error (its config carries the payload),
 * or the response. Only status + error code.
 */
import { mapActions } from 'vuex';
import moment from 'moment-timezone';
import PoweredByFooter from '@/components/PoweredByFooter.vue';
import { isDeadLinkResponse } from '@/utils/deadLink';

const STRINGS = {
  en: {
    title: 'Find your record',
    subtitle: 'Please confirm who you are to open your form',
    subtitleCode: 'Enter the code we sent you',
    dobLabel: 'Date of Birth',
    firstNameLabel: 'First name',
    lastNameLabel: 'Last name',
    findButton: 'Find my record',
    finding: 'Searching...',
    codeSentSms: 'We texted a 6-digit code to {destination}.',
    codeSentEmail: 'We emailed a 6-digit code to {destination}.',
    codeLabel: '6-digit code',
    continueButton: 'Continue',
    verifying: 'Checking...',
    resendButton: 'Send a new code',
    resending: 'Sending...',
    codeResent: 'We sent you a new code.',
    startOver: 'Start over',
    loading: 'Loading...',
    errDobRequired: 'Please enter your date of birth.',
    errDobFormat: 'Please enter your date of birth as {format}.',
    errFirstName: 'Please enter your first name.',
    errLastName: 'Please enter your last name.',
    errCode: 'Please enter the 6-digit code.',
    callOfficeAt: 'call the office at {phone}',
    callOffice: 'call the office',
    notFound: "We couldn't find a matching record. Please {call}.",
    rateLimited: 'Too many attempts. Please try again in 15 minutes or {call}.',
    wrongCode: "That code isn't right. {n} attempts left.",
    wrongCodeOne: "That code isn't right. 1 attempt left.",
    expired: 'That code expired. Please start again.',
    resendLimit: 'No more codes can be sent. Please {call}.',
    generic: 'Something went wrong. Please try again or {call}.',
    loadError: 'This link could not be opened. Please {call}.'
  },
  es: {
    title: 'Busque su expediente',
    subtitle: 'Confirme su identidad para abrir su formulario',
    subtitleCode: 'Ingrese el código que le enviamos',
    dobLabel: 'Fecha de nacimiento',
    firstNameLabel: 'Nombre',
    lastNameLabel: 'Apellido',
    findButton: 'Buscar mi expediente',
    finding: 'Buscando...',
    codeSentSms: 'Le enviamos un código de 6 dígitos por mensaje de texto a {destination}.',
    codeSentEmail: 'Le enviamos un código de 6 dígitos por correo electrónico a {destination}.',
    codeLabel: 'Código de 6 dígitos',
    continueButton: 'Continuar',
    verifying: 'Verificando...',
    resendButton: 'Enviar un código nuevo',
    resending: 'Enviando...',
    codeResent: 'Le enviamos un código nuevo.',
    startOver: 'Empezar de nuevo',
    loading: 'Cargando...',
    errDobRequired: 'Por favor ingrese su fecha de nacimiento.',
    errDobFormat: 'Por favor ingrese su fecha de nacimiento con el formato {format}.',
    errFirstName: 'Por favor ingrese su nombre.',
    errLastName: 'Por favor ingrese su apellido.',
    errCode: 'Por favor ingrese el código de 6 dígitos.',
    callOfficeAt: 'llame a la oficina al {phone}',
    callOffice: 'llame a la oficina',
    notFound: 'No encontramos un expediente que coincida. Por favor {call}.',
    rateLimited: 'Demasiados intentos. Intente de nuevo en 15 minutos o {call}.',
    wrongCode: 'Ese código no es correcto. Le quedan {n} intentos.',
    wrongCodeOne: 'Ese código no es correcto. Le queda 1 intento.',
    expired: 'Ese código venció. Por favor empiece de nuevo.',
    resendLimit: 'No se pueden enviar más códigos. Por favor {call}.',
    generic: 'Algo salió mal. Intente de nuevo o {call}.',
    loadError: 'No se pudo abrir este enlace. Por favor {call}.'
  }
};

function parseMaybeJson(value) {
  if (typeof value !== 'string') return value;
  try { return JSON.parse(value); } catch (e) { return value; }
}

/**
 * { status, code, attemptsLeft } from a failed call. The body may be an object, a JSON string, or a
 * JSON string wrapped again (createErrorResponse JSON.stringify's whatever it is given).
 */
function readApiError(err) {
  const response = err && err.response;
  const status = response ? response.status : null;
  let body = parseMaybeJson(response ? response.data : null);
  body = parseMaybeJson(body);
  let code = null;
  let attemptsLeft = null;
  const take = (obj) => {
    if (obj && typeof obj === 'object' && typeof obj.code === 'string') {
      code = obj.code;
      if (typeof obj.attemptsLeft === 'number') attemptsLeft = obj.attemptsLeft;
      return true;
    }
    return false;
  };
  if (!take(body) && body && typeof body === 'object') {
    ['body', 'message', 'error'].some(k => take(parseMaybeJson(body[k])));
    if (!code && typeof body.error === 'string' && /^[A-Z][A-Z_]+$/.test(body.error)) code = body.error;
  }
  if (!code && typeof body === 'string' && /^[A-Z][A-Z_]+$/.test(body.trim())) code = body.trim();
  if (!code && status === 429) code = 'RATE_LIMITED';
  if (!code && status === 410) code = 'EXPIRED';
  return { status, code, attemptsLeft };
}

export default {
  name: 'IdentifyPatient',
  props: ['urlID', 'siteID', 'formName'],
  components: { PoweredByFooter },

  data() {
    return {
      resolvedSiteID: null,
      resolvedName: null,
      siteInfo: null,
      loadFailed: false,
      step: 1,
      dob: '',
      firstName: '',
      lastName: '',
      code: '',
      challengeId: null,
      channel: 'sms',
      maskedDestination: '',
      loading: false,
      resending: false,
      message: '',
      messageIsError: true
    };
  },

  computed: {
    lang() {
      const q = (this.$route && this.$route.query) || {};
      return q.lang === 'es' ? 'es' : 'en';
    },
    title() {
      return this.formName || this.resolvedName || this.t('title');
    },
    officePhone() {
      const p = this.siteInfo && this.siteInfo.OfficePhone;
      return p && String(p).trim() ? String(p).trim() : '';
    }
  },

  created() {
    this.init();
  },

  methods: {
    ...mapActions('forms', ['identifyPatientForForm', 'verifyIdentifyCode']),
    ...mapActions('teamio', ['getSiteInfo']),
    ...mapActions('urlredirect', ['getLongUrl']),

    t(key, vars) {
      const table = STRINGS[this.lang] || STRINGS.en;
      let s = table[key] !== undefined ? table[key] : STRINGS.en[key];
      const all = Object.assign({}, vars || {});
      if (s.indexOf('{call}') !== -1) {
        all.call = this.officePhone
          ? table.callOfficeAt.replace('{phone}', this.officePhone)
          : table.callOffice;
      }
      Object.keys(all).forEach(k => { s = s.split('{' + k + '}').join(String(all[k])); });
      return s;
    },

    showError(key, vars) { this.message = this.t(key, vars); this.messageIsError = true; },
    showInfo(key, vars) { this.message = this.t(key, vars); this.messageIsError = false; },

    async init() {
      try {
        let siteID = this.siteID;
        if (!siteID) {
          // Refresh / direct open: params are gone. Re-resolve the generic link for its siteID + name.
          const res = await this.getLongUrl({ urlID: this.urlID });
          if (!res || isDeadLinkResponse(res) || res.status !== 200 || !res.data || !res.data.siteID) {
            this.failLoad();
            return;
          }
          siteID = res.data.siteID;
          this.resolvedName = res.data.type === 'packet' ? res.data.packetName : res.data.formName;
        }
        this.resolvedSiteID = siteID;
        const info = await this.getSiteInfo({ siteID });
        if (info && info.status === 200 && Array.isArray(info.data) && info.data[0]) {
          this.siteInfo = info.data[0];
        } else {
          this.failLoad();
        }
      } catch (e) {
        console.error('[IdentifyPatient] could not load the link', (e && e.response && e.response.status) || '');
        this.failLoad();
      }
    },

    failLoad() {
      this.loadFailed = true;
      this.showError('loadError');
    },

    async identify() {
      if (this.loading) return;
      const dob = (this.dob || '').trim();
      const firstName = (this.firstName || '').trim();
      const lastName = (this.lastName || '').trim();
      const dateFormat = this.siteInfo.DateFormat;

      if (!dob) return this.showError('errDobRequired');
      if (!moment(dob, dateFormat).isValid()) return this.showError('errDobFormat', { format: dateFormat });
      if (!firstName) return this.showError('errFirstName');
      if (!lastName) return this.showError('errLastName');

      this.message = '';
      this.loading = true;
      try {
        const res = await this.identifyPatientForForm({
          urlID: this.urlID,
          dob,
          dateFormat,
          firstName,
          lastName,
          lang: this.lang
        });
        const data = (res && res.data) || {};
        if (!data.challengeId) {
          this.showError('generic');
          return;
        }
        this.challengeId = data.challengeId;
        this.channel = data.channel === 'email' ? 'email' : 'sms';
        this.maskedDestination = data.maskedDestination || '';
        this.code = '';
        this.step = 2;
      } catch (err) {
        const e = readApiError(err);
        console.warn('[IdentifyPatient] identify failed', e.status, e.code);
        if (e.code === 'NOT_FOUND' || e.code === 'NO_CONTACT') this.showError('notFound');
        else if (e.code === 'RATE_LIMITED') this.showError('rateLimited');
        else this.showError('generic');
      } finally {
        this.loading = false;
      }
    },

    onCodeInput(event) {
      const digits = String(event.target.value || '').replace(/\D/g, '').slice(0, 6);
      this.code = digits;
      if (event.target.value !== digits) event.target.value = digits;
    },

    async verifyCode() {
      if (this.loading || this.resending) return;
      if (!/^\d{6}$/.test(this.code)) return this.showError('errCode');

      this.message = '';
      this.loading = true;
      try {
        const res = await this.verifyIdentifyCode({ challengeId: this.challengeId, code: this.code });
        const patientUrlID = res && res.data && res.data.urlID;
        if (!patientUrlID) {
          this.showError('generic');
          return;
        }
        this.continueToForm(patientUrlID);
      } catch (err) {
        const e = readApiError(err);
        console.warn('[IdentifyPatient] code check failed', e.status, e.code);
        if (e.code === 'WRONG_CODE') {
          if (e.attemptsLeft === 0) this.startOver('expired');
          else if (e.attemptsLeft === 1) this.showError('wrongCodeOne');
          else if (typeof e.attemptsLeft === 'number') this.showError('wrongCode', { n: e.attemptsLeft });
          else this.showError('generic');
          this.code = '';
        } else if (e.code === 'EXPIRED') {
          this.startOver('expired');
        } else if (e.code === 'RATE_LIMITED') {
          this.showError('rateLimited');
        } else {
          this.showError('generic');
        }
      } finally {
        this.loading = false;
      }
    },

    async resend() {
      if (this.loading || this.resending) return;
      this.message = '';
      this.resending = true;
      try {
        const res = await this.verifyIdentifyCode({ challengeId: this.challengeId, resend: true });
        const data = (res && res.data) || {};
        if (data.challengeId) this.challengeId = data.challengeId;
        if (data.channel) this.channel = data.channel === 'email' ? 'email' : 'sms';
        if (data.maskedDestination) this.maskedDestination = data.maskedDestination;
        this.code = '';
        this.showInfo('codeResent');
      } catch (err) {
        const e = readApiError(err);
        console.warn('[IdentifyPatient] resend failed', e.status, e.code);
        if (e.code === 'RESEND_LIMIT') this.showError('resendLimit');
        else if (e.code === 'EXPIRED') this.startOver('expired');
        else if (e.code === 'RATE_LIMITED') this.showError('rateLimited');
        else this.showError('generic');
      } finally {
        this.resending = false;
      }
    },

    // Back to step 1 (keeps what the patient typed; drops the challenge and code).
    startOver(messageKey) {
      this.step = 1;
      this.challengeId = null;
      this.code = '';
      this.maskedDestination = '';
      if (messageKey) this.showError(messageKey);
      else this.message = '';
    },

    // Hand off to the PATIENT-SPECIFIC link. The one-shot marker tells UrlRedirect to treat that one
    // resolve as already verified (it removes the marker). If storage throws, UrlRedirect simply sends
    // the patient through the usual VerifyForm step instead — never a dead end.
    continueToForm(patientUrlID) {
      try {
        sessionStorage.setItem('teamio_identified_' + patientUrlID, '1');
      } catch (e) {
        // storage blocked — UrlRedirect falls back to VerifyForm
      }
      const query = Object.assign({}, (this.$route && this.$route.query) || {});
      if (this.lang === 'es') query.lang = 'es';
      else delete query.lang;
      this.$router.replace({ path: '/l/' + patientUrlID, query });
    },

    getSiteDetails() {
      let retVal = '';
      if (this.siteInfo) {
        if (this.siteInfo.Name && this.siteInfo.Name.trim() !== '') {
          retVal = `<strong>${this.siteInfo.Name}</strong>`;
        }
        if (this.siteInfo.Address1 && this.siteInfo.Address1.trim() !== '') {
          retVal = retVal + `<br>${this.siteInfo.Address1}`;
          if (this.siteInfo.Address2 && this.siteInfo.Address2.trim() !== '') {
            retVal = retVal + `<br>${this.siteInfo.Address2}`;
          }
          retVal = retVal + `<br>${this.siteInfo.City}, ${this.siteInfo.State} ${this.siteInfo.Zip}`;
        }
        if (this.siteInfo.OfficePhone && this.siteInfo.OfficePhone.trim() !== '') {
          retVal = retVal + `<br><strong>Phone:</strong> ${this.siteInfo.OfficePhone}`;
        }
      }
      return retVal;
    }
  }
};
</script>

<style lang="scss" scoped>
.identify-page {
  min-height: 100vh;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  font-family: 'Inter', 'Segoe UI', Roboto, -apple-system, BlinkMacSystemFont, sans-serif;
}

.identify-container {
  width: 100%;
  max-width: 440px;
  margin: 0 auto;
}

/* Logo — height-capped (logo-size-cap-guardrail: max-height <= 120px + object-fit: contain). */
.logo-section {
  text-align: center;
  margin-bottom: 24px;
}

.logo {
  max-width: 200px;
  max-height: 80px;
  width: auto;
  height: auto;
  object-fit: contain;
  display: block;
  margin: 0 auto;
}

.identify-card {
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  overflow: hidden;
}

.card-header {
  background: linear-gradient(135deg, #3abf94 0%, #2d9a77 100%);
  padding: 28px 32px;
  text-align: center;
}

.card-title {
  color: #ffffff;
  font-size: 21px;
  font-weight: 700;
  margin: 0 0 8px 0;
  line-height: 1.3;
}

.card-subtitle {
  color: rgba(255, 255, 255, 0.9);
  font-size: 15px;
  margin: 0;
}

.card-body {
  padding: 32px;
}

.message {
  border-radius: 10px;
  padding: 12px 14px;
  margin-bottom: 20px;
  font-size: 15px;
  line-height: 1.45;
}

.message-error {
  background-color: #fef2f2;
  border: 1px solid #fecaca;
  color: #991b1b;
}

.message-info {
  background-color: #f0fdf4;
  border: 1px solid #bbf7d0;
  color: #166534;
}

.form-group {
  margin-bottom: 24px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
}

.label-text {
  font-weight: 600;
  color: #334155;
  font-size: 15px;
  display: block;
}

.input-wrapper {
  position: relative;
}

.input-icon {
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
  font-size: 18px;
  pointer-events: none;
  z-index: 1;
}

.styled-input {
  width: 100%;
  padding: 12px 14px 12px 44px;
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  /* 16px, never 1rem (the app root is 14px): iOS Safari zooms in on focus below 16px and the
     zoom survives the hop into the form, which then opens "cut off". */
  font-size: 16px;
  color: #334155;
  background-color: #f8fafc;
  transition: all 0.2s ease;
}

.styled-input:focus {
  border-color: #3abf94;
  box-shadow: 0 0 0 4px rgba(58, 191, 148, 0.1);
  background-color: #ffffff;
  outline: none;
}

.styled-input::placeholder {
  color: #94a3b8;
}

.code-input {
  padding-left: 14px;
  text-align: center;
  letter-spacing: 8px;
  font-size: 22px;
  font-weight: 600;
}

.code-sent {
  color: #334155;
  font-size: 15px;
  margin-bottom: 20px;
}

.primary-btn {
  width: 100%;
  padding: 14px 24px;
  font-size: 16px;
  font-weight: 600;
  border-radius: 10px;
  background: linear-gradient(135deg, #3abf94 0%, #2d9a77 100%);
  border: none;
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 4px rgba(58, 191, 148, 0.3);
  margin-top: 8px;
  cursor: pointer;
}

.primary-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.secondary-actions {
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 16px;
}

.link-btn {
  background: none;
  border: none;
  padding: 4px 0;
  color: #2d9a77;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  text-decoration: underline;
}

.link-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.office-info-section {
  padding: 20px 32px 24px;
  border-top: 1px solid #e2e8f0;
  background-color: #f8fafc;
}

.office-details {
  font-size: 14px;
  color: #64748b;
  line-height: 1.7;
  text-align: center;
}

.office-details ::v-deep strong {
  color: #334155;
  font-weight: 600;
}

.loading-state {
  text-align: center;
  padding: 48px 24px;
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
}

.spinner {
  width: 48px;
  height: 48px;
  border: 4px solid #e2e8f0;
  border-top-color: #3abf94;
  border-radius: 50%;
  animation: identify-spin 0.8s linear infinite;
  margin: 0 auto 16px;
}

@keyframes identify-spin {
  to {
    transform: rotate(360deg);
  }
}

.loading-state p {
  color: #64748b;
  font-size: 15px;
  margin: 0;
}

@media (max-width: 480px) {
  .identify-page {
    padding: 16px;
  }

  .card-header {
    padding: 24px;
  }

  .card-title {
    font-size: 18px;
  }

  .card-body {
    padding: 24px;
  }

  .office-info-section {
    padding: 16px 24px 20px;
  }
}
</style>
